"use client";
import { useEffect, useState, useRef, useMemo } from "react";
import { useSearchParams } from "next/navigation";
import dynamic from "next/dynamic";
import { SkeletonProduct, SkeletonBanner, SkeletonCategory } from "@/components/SkeletonLoaders";
import ProductCard from "@/components/ProductCard";
import {
    getProductsCount,
    getProductsPaginated,
    searchProductsByName,
} from "@/firebase/firestoreServices";
import type { Product, Banner, Category } from "@/types";
import { motion } from "framer-motion";
import { useOutlet } from "@/context/OutletContext";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import LocationPrompt from "@/components/LocationPrompt";

import BannerSlider from "@/components/BannerSlider";
import CategorySection from "@/components/CategorySection";
const CitiesWeServe = dynamic(() => import("@/components/CitiesWeServe"));

const PRODUCTS_PER_PAGE = 12;
const SEARCH_DEBOUNCE_MS = 450;

interface HomeContentProps {
    initialBanners?: Banner[];
    initialCategories?: Category[];
    initialProducts?: Product[];
    initialLastDoc?: unknown;
}

export const HomeContent = ({ 
    initialBanners = [], 
    initialCategories = [], 
    initialProducts = [], 
    initialLastDoc = null 
}: HomeContentProps) => {
    const { selectedOutlet } = useOutlet();

    // ── Browse state ─────────────────────────────────────────────────────────
    const [products, setProducts] = useState<Product[]>(initialProducts);
    const [loading, setLoading] = useState(initialProducts.length === 0);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const pageCursors = useRef<{ [key: number]: unknown }>({ 
        1: null,
        2: initialLastDoc 
    });

    // ── Search state ──────────────────────────────────────────────────────────
    const [searchResults, setSearchResults] = useState<Product[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const searchParams = useSearchParams();
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        if (searchParams) {
            setSearchQuery(searchParams.get("q")?.trim() || "");
        }
    }, [searchParams]);

    const isSearching = searchQuery.length > 0;

    // ── Firebase search — fires only when user types, reads max 15 docs ───────
    useEffect(() => {
        if (!isSearching) {
            setSearchResults([]);
            return;
        }
        if (!selectedOutlet?.id) return;

        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        setSearchLoading(true);

        debounceTimer.current = setTimeout(async () => {
            const results = await searchProductsByName(searchQuery, selectedOutlet.id, 15);
            setSearchResults(results);
            setSearchLoading(false);
        }, SEARCH_DEBOUNCE_MS);

        return () => {
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
        };
    }, [searchQuery, selectedOutlet?.id, isSearching]);

    // ── Total count (for pagination) ──────────────────────────────────────────
    useEffect(() => {
        if (!selectedOutlet?.id) { setTotalCount(0); return; }
        getProductsCount(selectedOutlet.id).then(setTotalCount);
    }, [selectedOutlet?.id]);

    const initialFetchSkipped = useRef(false);

    // ── Paginated browse — paused while searching ─────────────────────────────
    useEffect(() => {
        if (isSearching) return; // 🔒 no extra reads while searching
        if (!selectedOutlet?.id) return; // Wait for outlet to be determined

        const fetchPage = async () => {
            // Skip fetching on initial load if we're on page 1, using the default outlet, and already have initialProducts
            if (currentPage === 1 && !initialFetchSkipped.current && selectedOutlet.id === "vK6SnoP7D7mYQ3IqXp0O" && initialProducts.length > 0) {
                initialFetchSkipped.current = true;
                return;
            }

            setLoading(true);
            const cursor = pageCursors.current[currentPage];
            const { products: fetched, lastDoc } = await getProductsPaginated(
                PRODUCTS_PER_PAGE, cursor, selectedOutlet.id
            );
            setProducts(fetched);
            if (lastDoc && !pageCursors.current[currentPage + 1]) {
                pageCursors.current[currentPage + 1] = lastDoc;
            }
            setLoading(false);
            initialFetchSkipped.current = true;
        };
        fetchPage();
    }, [currentPage, selectedOutlet?.id, isSearching, initialProducts.length]);

    // ── Reset on outlet change ────────────────────────────────────────────────
    useEffect(() => {
        setCurrentPage(1);
        pageCursors.current = { 1: null };
    }, [selectedOutlet?.id]);

    const totalPages = Math.ceil(totalCount / PRODUCTS_PER_PAGE);

    const goToPage = (page: number) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
        window.scrollTo({ top: 400, behavior: "smooth" });
    };

    const getPageNumbers = useMemo(() => {
        const pages: (number | "...")[] = [];
        if (totalPages <= 5) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1);
            if (currentPage > 3) pages.push("...");
            for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                pages.push(i);
            }
            if (currentPage < totalPages - 2) pages.push("...");
            pages.push(totalPages);
        }
        return pages;
    }, [totalPages, currentPage]);

    return (
        <div className="space-y-12 pb-8">
            <LocationPrompt />
            {!isSearching && <BannerSlider initialBanners={initialBanners} />}
            {!isSearching && <CategorySection initialCategories={initialCategories} />}

            {/* ── Search Results ──────────────────────────────────────────────── */}
            {isSearching && (
                <section className="space-y-5">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="flex items-center gap-3"
                    >
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Search className="w-4 h-4 text-primary" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-foreground leading-tight">
                                Results for &ldquo;<span className="text-primary">{searchQuery}</span>&rdquo;
                            </h2>
                            {!searchLoading && (
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    {searchResults.length === 0
                                        ? "No products found"
                                        : `${searchResults.length} product${searchResults.length !== 1 ? "s" : ""} found`}
                                </p>
                            )}
                        </div>
                    </motion.div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {searchLoading
                            ? Array.from({ length: 6 }).map((_, i) => <SkeletonProduct key={i} />)
                            : searchResults.length > 0
                                ? searchResults.map((p) => <ProductCard key={p.id} product={p} />)
                                : (
                                    <div className="col-span-full flex flex-col items-center py-16 gap-3 text-center">
                                        <p className="text-5xl">🔍</p>
                                        <p className="text-lg font-medium text-foreground">No products found</p>
                                        <p className="text-sm text-muted-foreground">
                                            Try a different keyword — e.g. "chicken", "mutton"
                                        </p>
                                    </div>
                                )}
                    </div>
                </section>
            )}

            {/* ── All Products (browse mode) ───────────────────────────────────── */}
            {!isSearching && (
                <section className="space-y-6">
                    <div>
                        <h2 className="text-xl md:text-2xl font-bold text-foreground">All Products</h2>
                        <p className="text-sm text-muted-foreground mt-0.5">Fresh &amp; delivered daily</p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {loading
                            ? Array.from({ length: 6 }).map((_, i) => <SkeletonProduct key={i} />)
                            : products.length > 0
                                ? products.map((p) => <ProductCard key={p.id} product={p} />)
                                : (
                                    <div className="col-span-full text-center py-16 space-y-3">
                                        <p className="text-5xl">🍖</p>
                                        <p className="text-lg font-medium text-foreground">No products available yet</p>
                                        <p className="text-sm text-muted-foreground">Products will appear here once added</p>
                                    </div>
                                )}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="flex items-center justify-center gap-2 pt-6"
                        >
                            <button
                                aria-label="Previous Page"
                                onClick={() => goToPage(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="p-2 rounded-lg border border-border hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>

                            {getPageNumbers.map((page: number | "...", idx: number) =>
                                page === "..." ? (
                                    <span key={`ellipsis-${idx}`} className="px-2 text-muted-foreground text-sm">…</span>
                                ) : (
                                    <button
                                        key={page}
                                        onClick={() => goToPage(page as number)}
                                        className={`w-9 h-9 rounded-lg text-sm font-medium transition-all duration-200 ${currentPage === page
                                            ? "bg-primary text-primary-foreground shadow-md"
                                            : "border border-border hover:bg-muted text-foreground"
                                            }`}
                                    >
                                        {page}
                                    </button>
                                )
                            )}

                            <button
                                aria-label="Next Page"
                                onClick={() => goToPage(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="p-2 rounded-lg border border-border hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </motion.div>
                    )}
                </section>
            )}

            {!isSearching && <CitiesWeServe />}
        </div>
    );
};
